import React, { useEffect, useMemo, useState } from 'react';
import { MapPin, Save, Trash2, RefreshCw, ToggleLeft, ToggleRight, Map } from 'lucide-react';
import {
  createCityDeliveryCharge,
  deleteCityDeliveryCharge,
  fetchCityDeliveryCharges,
  toggleCityDeliveryChargeStatus,
  updateCityDeliveryCharge,
} from '../api';

const Pincode = () => {
  const [city, setCity] = useState('');
  const [pincode, setPincode] = useState('');
  const [deliveryCharge, setDeliveryCharge] = useState('');
  const [rows, setRows] = useState([]);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setError('');
      setIsLoading(true);
      try {
        const list = await fetchCityDeliveryCharges();
        if (cancelled) return;
        setRows(
          (list || []).map((r) => ({
            id: r.id,
            city: r.cityname,
            pincode: r.pincode,
            deliveryCharge: r.delivery_charge,
            isActive: r.is_active,
            updatedAt: r.updated_at || r.updatedAt || r.created_at || r.createdAt,
          }))
        );
      } catch (e) {
        if (cancelled) return;
        console.error('Failed to load city delivery charges', e);
        setError(e?.response?.data?.error || e?.response?.data?.message || e?.message || 'Failed to load delivery charges');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const normalizedPincode = useMemo(() => String(pincode || '').trim(), [pincode]);

  const onSave = async () => {
    setError('');
    const c = city.trim();
    const pc = normalizedPincode;
    const charge = Number(deliveryCharge);

    if (!c) return setError('City is required');
    if (!/^\d{6}$/.test(pc)) return setError('Pincode must be 6 digits');
    if (!Number.isFinite(charge) || charge < 0) return setError('Delivery charge must be 0 or more');

    setIsSaving(true);
    try {
      const existing = rows.find((r) => String(r.pincode) === pc);
      if (existing?.id) {
        await updateCityDeliveryCharge(existing.id, {
          cityname: c,
          pincode: pc,
          delivery_charge: charge,
        });
      } else {
        await createCityDeliveryCharge({
          cityname: c,
          pincode: pc,
          delivery_charge: charge,
        });
      }
      const list = await fetchCityDeliveryCharges();
      setRows(
        (list || []).map((r) => ({
          id: r.id,
          city: r.cityname,
          pincode: r.pincode,
          deliveryCharge: r.delivery_charge,
          isActive: r.is_active,
          updatedAt: r.updated_at || r.updatedAt || r.created_at || r.createdAt,
        }))
      );

      setCity('');
      setPincode('');
      setDeliveryCharge('');
    } catch (e) {
      console.error('Failed to save city delivery charge', e);
      setError(e?.response?.data?.error || e?.response?.data?.message || e?.message || 'Save failed');
    } finally {
      setIsSaving(false);
    }
  };

  const onDelete = async (id) => {
    setError('');
    try {
      await deleteCityDeliveryCharge(id);
      setRows((prev) => prev.filter((r) => r.id !== id));
    } catch (e) {
      console.error('Failed to delete city delivery charge', e);
      setError(e?.response?.data?.error || e?.response?.data?.message || e?.message || 'Delete failed');
    }
  };

  const onToggle = async (id, currentIsActive) => {
    setError('');
    if (id === undefined || id === null || String(id).trim() === '') {
      setError('Cannot toggle status: missing id from API response');
      return;
    }
    setRows((prev) =>
      prev.map((r) => (r.id === id ? { ...r, isActive: !currentIsActive } : r))
    );
    try {
      const res = await toggleCityDeliveryChargeStatus(id, !currentIsActive);
      if (res && typeof res === 'object' && res.id !== undefined) {
        setRows((prev) =>
          prev.map((r) =>
            r.id === id
              ? {
                  ...r,
                  city: res.cityname ?? r.city,
                  pincode: res.pincode ?? r.pincode,
                  deliveryCharge: res.delivery_charge ?? r.deliveryCharge,
                  isActive: res.is_active ?? r.isActive,
                  updatedAt: res.updated_at || res.updatedAt || r.updatedAt,
                }
              : r
          )
        );
      }
    } catch (e) {
      console.error('Failed to toggle status', e);
      setError(e?.response?.data?.error || e?.response?.data?.message || e?.message || 'Toggle failed');
      setRows((prev) =>
        prev.map((r) => (r.id === id ? { ...r, isActive: currentIsActive } : r))
      );
    }
  };

  const handleRefresh = async () => {
    setError('');
    setIsLoading(true);
    try {
      const list = await fetchCityDeliveryCharges();
      setRows(
        (list || []).map((r) => ({
          id: r.id,
          city: r.cityname,
          pincode: r.pincode,
          deliveryCharge: r.delivery_charge,
          isActive: r.is_active,
          updatedAt: r.updated_at || r.updatedAt || r.created_at || r.createdAt,
        }))
      );
    } catch (e) {
      setError(e?.response?.data?.error || e?.response?.data?.message || e?.message || 'Refresh failed');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="ui-page animate-fade-in">
      {/* HEADER CONTROLS */}
      <div className="mb-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-extrabold text-slate-800 tracking-tight">Pincode Delivery Charges</h1>
          <p className="text-slate-500 mt-1 font-medium text-sm">Configure shipping costs based on destination pincodes.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        
        {/* ADD / EDIT PINCODE FORM */}
        <div className="lg:col-span-1">
          <div className="ui-card ui-card-pad bg-white border border-slate-100 shadow-sm sticky top-6">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-emerald-600 to-green-700 flex items-center justify-center text-white shadow-lg shadow-emerald-600/20">
                <MapPin size={16} />
              </div>
              <h3 className="text-base font-extrabold text-slate-800">Add or Edit Pincode</h3>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">City Name</label>
                <input
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  className="ui-input w-full text-xs"
                  placeholder="e.g. Chennai"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Pincode (6 digits)</label>
                <input
                  value={pincode}
                  onChange={(e) => setPincode(e.target.value)}
                  className="ui-input w-full text-xs"
                  placeholder="e.g. 600001"
                  inputMode="numeric"
                  maxLength={6}
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Delivery Charge (₹)</label>
                <input
                  value={deliveryCharge}
                  onChange={(e) => setDeliveryCharge(e.target.value)}
                  className="ui-input w-full text-xs"
                  placeholder="e.g. 50"
                  inputMode="decimal"
                />
              </div>
            </div>

            {error && (
              <p className="mt-4 p-3 bg-rose-50 border border-rose-100 rounded-xl text-xs text-rose-700 font-semibold animate-fade-in">
                ⚠️ {error}
              </p>
            )}

            <div className="mt-6 flex gap-2">
              <button
                type="button"
                onClick={handleRefresh}
                className="ui-btn-secondary !py-2 !px-3.5 !rounded-xl flex-1 text-xs"
                disabled={isLoading || isSaving}
              >
                <RefreshCw size={14} className={isLoading ? 'animate-spin' : ''} /> 
                Refresh
              </button>
              <button
                onClick={onSave}
                className="ui-btn-primary !py-2 !px-4 !rounded-xl flex-1 text-xs"
                disabled={isLoading || isSaving}
              >
                <Save size={14} /> 
                {isSaving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>

        {/* PINCODE LIST TABLE */}
        <div className="lg:col-span-2">
          <div className="ui-card overflow-hidden border border-slate-100 shadow-sm bg-white">
            <div className="px-6 py-5 border-b border-slate-100 flex items-center justify-between bg-gradient-to-r from-emerald-50/10 to-white">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-emerald-50 text-emerald-700 flex items-center justify-center font-bold">
                  <Map size={15} />
                </div>
                <h3 className="text-sm font-extrabold text-slate-800">
                  Active Shipping Zones
                </h3>
              </div>
              <span className="text-[10px] font-extrabold bg-emerald-50 text-emerald-700 border border-emerald-100/60 px-2 py-0.5 rounded-md">
                {rows.length} Configured
              </span>
            </div>

            <div className="overflow-x-auto">
              <table className="ui-table">
                <thead>
                  <tr className="bg-slate-50/20">
                    <th className="ui-th !py-3 w-8">#</th>
                    <th className="ui-th !py-3 text-left">City</th>
                    <th className="ui-th !py-3 text-left">Pincode</th>
                    <th className="ui-th !py-3 text-right">Delivery Charge</th>
                    <th className="ui-th !py-3 text-center">Status</th>
                    <th className="ui-th !py-3 text-center">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {rows.map((r, i) => (
                    <tr key={r.id || i} className="hover:bg-emerald-50/10 transition-colors">
                      <td className="ui-td font-semibold text-slate-400">{i + 1}</td>
                      <td className="ui-td font-bold text-slate-800">{r.city}</td>
                      <td className="ui-td text-slate-600 font-semibold">{r.pincode}</td>
                      <td className="ui-td text-right text-emerald-600 font-extrabold">
                        ₹{Number(r.deliveryCharge || 0).toLocaleString()}
                      </td>
                      <td className="ui-td text-center">
                        <span
                          className={`inline-flex px-2.5 py-0.5 rounded-full text-[10px] font-bold border ${
                            r.isActive
                              ? 'bg-emerald-50 text-emerald-700 border-emerald-100'
                              : 'bg-slate-50 text-slate-500 border-slate-200'
                          }`}
                        >
                          {r.isActive ? 'Active' : 'Disabled'}
                        </span>
                      </td>
                      <td className="ui-td text-center">
                        <div className="flex items-center justify-center gap-1.5">
                          <button
                            type="button"
                            onClick={() => onToggle(r.id, r.isActive)}
                            className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="Toggle Delivery Zone Status"
                          >
                            {r.isActive ? <ToggleRight size={18} className="text-emerald-500" /> : <ToggleLeft size={18} className="text-slate-300" />}
                          </button>
                          <button
                            onClick={() => onDelete(r.id)}
                            className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors"
                            title="Delete Delivery Zone"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}

                  {rows.length === 0 && (
                    <tr>
                      <td className="ui-td text-center text-slate-400 py-12" colSpan={6}>
                        {isLoading ? (
                          <div className="flex items-center justify-center gap-2">
                            <div className="w-5 h-5 border-2 border-slate-200 border-t-emerald-600 rounded-full animate-spin" />
                            <span>Loading shipping rules...</span>
                          </div>
                        ) : (
                          'No active delivery pincodes saved yet.'
                        )}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default Pincode;
